// src/hooks/useAssessment/useAssessmentSubmission.js
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildAssessmentPayload } from '../../utils/assessment/transformers';
import { assessmentService } from '../../utils/apiService';
import logger from '../../utils/logger';

/**
 * Hook to manage assessment submission lifecycle.
 *
 * Responsibilities:
 * - Build and submit the assessment payload
 * - Manage submitting state
 * - Perform all error handling and logging
 * - Expose handleSubmitAssessment (and a placeholder handleSave)
 *
 * Note: Error UI (JSX) is still produced here exactly as it was in Assessment.js,
 * but the main layout JSX remains in the component.
 */
export const useAssessmentSubmission = ({
  facilityInfo,
  energyBaseline,
  answers,
  questions,
  user,
  type,
  region,
  navigate,
  setError
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitAssessment = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);

    // Validate all required questions are answered
    const unansweredRequired = questions?.filter(
      (q) => q.required && !answers[q.id]
    );
    if (unansweredRequired && unansweredRequired.length > 0) {
      setError(
        `Please answer all required questions. ${unansweredRequired.length} required question(s) remaining.`
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const assessmentData = buildAssessmentPayload({
        facilityInfo,
        energyBaseline,
        answers,
        questions,
        user,
        type,
        region
      });

      logger.log('Submitting assessment with user_id:', assessmentData.user_id);
      logger.log('Assessment data:', assessmentData);

      const result = await assessmentService.submit(assessmentData);

      logger.log('Full result:', result);
      logger.log('Result error:', result.error);
      logger.log('Result error detail:', result.error?.detail);

      if (result.success) {
        const assessmentId = result.data.assessment_id;
        navigate(`/assessment/complete/${assessmentId}`);
      } else {
        const errorData = result.error;
        const detail = errorData?.detail;

        logger.log('Error detail object:', detail);

        if (detail && typeof detail === 'object' && detail.error === 'duplicate_assessment') {
          setError(
            <div className="space-y-3">
              <div className="font-semibold text-red-800 dark:text-red-200">{detail.message}</div>
              <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded">
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>What you can do:</strong>
                </div>
                <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-none">
                  <li>
                    • View your existing {detail.existing_industry} assessment in the{' '}
                    <Link
                      to="/dashboard"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li>• Complete an assessment for a different industry</li>
                  <li>• Contact support if you need to update your existing assessment</li>
                </ul>
              </div>
            </div>
          );
        } else if (detail?.message) {
          setError(detail.message);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else if (errorData?.message) {
          setError(errorData.message);
        } else if (typeof errorData === 'string') {
          setError(errorData);
        } else {
          setError('Failed to submit assessment. Please try again.');
        }
      }
    } catch (error) {
      logger.error('Assessment submission error:', error);
      logger.error('Error response:', error.response);

      // Handle 409 Conflict (duplicate assessment) and 400 Bad Request
      if (error.response?.status === 409 || error.response?.status === 400) {
        const errorData = error.response?.data;
        let detail = errorData?.detail;

        // If detail is not available, check message field (some error wrappers put it there)
        // Also check if detail or message is a string containing a dict
        if (!detail && errorData?.message) {
          detail = errorData.message;
        } else if (detail && typeof detail === 'string' && detail.includes('error')) {
          // detail is already a string, we'll parse it below
        } else if (errorData?.message && typeof errorData.message === 'string' && errorData.message.includes('error') && !detail) {
          detail = errorData.message;
        }
        
        logger.log('Error data structure:', { 
          status: error.response?.status, 
          detail: detail, 
          errorData: errorData,
          detailType: typeof detail,
          fullResponse: JSON.stringify(error.response?.data, null, 2)
        });

        // Parse detail if it's a string containing Python dict or JSON
        if (typeof detail === 'string') {
          try {
            // Check if it's a Python dict format (single quotes) or JSON (double quotes)
            if (detail.includes('error') && (detail.includes("'") || detail.includes('"'))) {
              // Use a more sophisticated approach: parse Python dict manually
              // This handles values with quotes inside them
              const parsePythonDict = (str) => {
                const result = {};
                // Remove outer braces and whitespace
                let content = str.trim().replace(/^\{|\}$/g, '').trim();
                
                // Split by commas, but be careful with commas inside quoted strings
                const fields = [];
                let current = '';
                let inQuotes = false;
                let quoteChar = null;
                let depth = 0;
                
                for (let i = 0; i < content.length; i++) {
                  const char = content[i];
                  const nextChar = content[i + 1];
                  
                  if ((char === "'" || char === '"') && (i === 0 || content[i - 1] !== '\\')) {
                    if (!inQuotes) {
                      inQuotes = true;
                      quoteChar = char;
                    } else if (char === quoteChar) {
                      inQuotes = false;
                      quoteChar = null;
                    }
                  } else if (char === '{' && !inQuotes) {
                    depth++;
                  } else if (char === '}' && !inQuotes) {
                    depth--;
                  } else if (char === ',' && !inQuotes && depth === 0) {
                    fields.push(current.trim());
                    current = '';
                    continue;
                  }
                  current += char;
                }
                if (current.trim()) {
                  fields.push(current.trim());
                }
                
                // Parse each field
                fields.forEach(field => {
                  const colonIndex = field.indexOf(':');
                  if (colonIndex === -1) return;
                  
                  let key = field.substring(0, colonIndex).trim();
                  let value = field.substring(colonIndex + 1).trim();
                  
                  // Remove quotes from key
                  key = key.replace(/^['"]|['"]$/g, '');
                  
                  // Parse value
                  if (value.startsWith("'") && value.endsWith("'")) {
                    value = value.slice(1, -1);
                  } else if (value.startsWith('"') && value.endsWith('"')) {
                    value = value.slice(1, -1);
                  } else if (value === 'True') {
                    value = true;
                  } else if (value === 'False') {
                    value = false;
                  } else if (value === 'None') {
                    value = null;
                  } else if (!isNaN(value) && value !== '') {
                    value = Number(value);
                  }
                  
                  result[key] = value;
                });
                
                return result;
              };
              
              detail = parsePythonDict(detail);
            }
          } catch (e) {
            // If parsing fails, try to extract fields manually with better regex
            logger.warn('Could not parse error detail, trying regex extraction:', e);
            logger.warn('Original detail string:', detail);
            
            // Extract fields using a more sophisticated approach that handles mixed quotes
            const extractField = (fieldName) => {
              // Try multiple patterns to handle different quote combinations
              // Most common: key in single quotes, value in double quotes (for messages with quotes)
              const patterns = [
                // Key in single quotes, value in double quotes (most common for messages with quotes)
                new RegExp(`'${fieldName}'\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`),
                // Key in single quotes, value in single quotes
                new RegExp(`'${fieldName}'\\s*:\\s*'((?:[^'\\\\]|\\\\.)*)'`),
                // Key in double quotes, value in double quotes
                new RegExp(`"${fieldName}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`),
                // Key in double quotes, value in single quotes
                new RegExp(`"${fieldName}"\\s*:\\s*'((?:[^'\\\\]|\\\\.)*)'`),
              ];
              
              for (const pattern of patterns) {
                const match = detail.match(pattern);
                if (match && match[1]) {
                  return match[1].replace(/\\'/g, "'").replace(/\\"/g, '"');
                }
              }
              return null;
            };
            
            const message = extractField('message');
            const error = extractField('error');
            const id = extractField('existing_assessment_id');
            
            if (message || error) {
              detail = {
                message: message || 'Duplicate assessment detected',
                error: error || 'duplicate_assessment',
                existing_assessment_id: id || null
              };
            }
          }
        }

        // If detail is still a string after parsing attempts, try one more extraction
        if (typeof detail === 'string' && detail.includes('error')) {
          logger.warn('Detail is still a string after parsing, attempting final extraction');
          // Try more specific regex patterns that handle mixed quotes
          const extractMessage = () => {
            // Try: 'message': "value" (single quote key, double quote value)
            let match = detail.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/);
            if (match) return match[1];
            // Try: 'message': 'value' (both single quotes)
            match = detail.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/);
            if (match) return match[1];
            // Try: "message": "value" (both double quotes)
            match = detail.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            if (match) return match[1];
            return null;
          };
          
          const extractError = () => {
            const patterns = [
              /'error'\s*:\s*"([^"]+)"/,
              /'error'\s*:\s*'([^']+)'/,
              /"error"\s*:\s*"([^"]+)"/,
            ];
            for (const pattern of patterns) {
              const match = detail.match(pattern);
              if (match) return match[1];
            }
            return null;
          };
          
          const extractId = () => {
            const patterns = [
              /'existing_assessment_id'\s*:\s*"([^"]+)"/,
              /'existing_assessment_id'\s*:\s*'([^']+)'/,
              /"existing_assessment_id"\s*:\s*"([^"]+)"/,
            ];
            for (const pattern of patterns) {
              const match = detail.match(pattern);
              if (match) return match[1];
            }
            return null;
          };
          
          const message = extractMessage();
          const error = extractError();
          const id = extractId();
          
          if (message || error) {
            detail = {
              message: message || 'Duplicate assessment detected',
              error: error || 'duplicate_assessment',
              existing_assessment_id: id || null
            };
            logger.log('Final extraction result:', detail);
          }
        }

        // Final safety check: if detail is still a string that looks like a dict, force extraction
        if (typeof detail === 'string' && detail.includes('error') && detail.includes('message')) {
          logger.warn('Detail is still a string after all parsing attempts, forcing extraction');
          const forceExtractMessage = () => {
            // Try all possible patterns
            const patterns = [
              /'message'\s*:\s*"((?:[^"\\]|\\.)*)"/,  // 'message': "value"
              /'message'\s*:\s*'((?:[^'\\]|\\.)*)'/,  // 'message': 'value'
              /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/,  // "message": "value"
            ];
            for (const pattern of patterns) {
              const match = detail.match(pattern);
              if (match && match[1]) return match[1];
            }
            return null;
          };
          
          const extractedMessage = forceExtractMessage();
          if (extractedMessage) {
            detail = {
              message: extractedMessage,
              error: 'Duplicate Assessment',
              existing_assessment_id: detail.match(/'existing_assessment_id'\s*:\s*['"]([^'"]+)['"]/)?.[1] || null
            };
            logger.log('Force extracted detail:', detail);
          }
        }

        // Handle duplicate assessment error
        if (detail && (typeof detail === 'object') && (detail.error === 'duplicate_assessment' || detail.error === 'Duplicate assessment' || detail.error === 'Duplicate Assessment')) {
          const errorMessage = detail.message || 'You have already submitted this assessment. Please view your existing results instead.';
          const existingAssessmentId = detail.existing_assessment_id;
          const existingIndustry = detail.existing_industry || facilityInfo.majorIndustry || facilityInfo.industry || 'this industry';
          
          setError(
            <div className="space-y-3">
              <p className="font-semibold text-red-800 dark:text-red-200">{errorMessage}</p>
              <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>What you can do:</strong>
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
                  <li>
                    • View your existing {existingIndustry} assessment in the{' '}
                    <Link
                      to="/dashboard"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                    >
                      Dashboard
                    </Link>
                  </li>
                  {existingAssessmentId && (
                    <li>
                      • <Link
                        to={`/assessment/complete/${existingAssessmentId}`}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                      >
                        View your existing assessment results
                      </Link>
                    </li>
                  )}
                  <li>• Complete an assessment for a different industry</li>
                  <li>• Contact support if you need to update your existing assessment</li>
                </ul>
              </div>
            </div>
          );
        } else if (detail && typeof detail === 'object' && detail.message) {
          setError(detail.message);
        } else if (typeof detail === 'string') {
          // Last resort: try to extract just the message from the string
          // Handle: 'message': "value" format
          let messageMatch = detail.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/);
          if (!messageMatch) {
            messageMatch = detail.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/);
          }
          if (!messageMatch) {
            messageMatch = detail.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
          }
          
          if (messageMatch && messageMatch[1]) {
            setError(messageMatch[1]);
          } else {
            // If we can't extract, show a generic message instead of raw string
            setError('You have already submitted this assessment. Please view your existing results in the Dashboard.');
          }
        } else if (errorData?.message) {
          // Check if errorData.message is a raw dict string
          if (typeof errorData.message === 'string' && (errorData.message.includes("'error'") || errorData.message.includes('"error"'))) {
            // Try to extract message from the string
            let messageMatch = errorData.message.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/);
            if (!messageMatch) {
              messageMatch = errorData.message.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/);
            }
            if (!messageMatch) {
              messageMatch = errorData.message.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            }
            setError(messageMatch && messageMatch[1] ? messageMatch[1] : 'You have already submitted this assessment. Please view your existing results in the Dashboard.');
          } else {
            setError(errorData.message);
          }
        } else {
          setError('Failed to submit assessment. Please try again.');
        }
      } else {
        // For other errors, try to extract the message
        let errorMessage = error.response?.data?.detail?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.detail ||
                           error.message ||
                           'Failed to submit assessment. Please check your connection and try again.';
        
        // If errorMessage is a raw dict string, try to extract the message
        if (typeof errorMessage === 'string' && (errorMessage.includes("'error'") || errorMessage.includes('"error"'))) {
          let messageMatch = errorMessage.match(/'message'\s*:\s*"((?:[^"\\]|\\.)*)"/);
          if (!messageMatch) {
            messageMatch = errorMessage.match(/'message'\s*:\s*'((?:[^'\\]|\\.)*)'/);
          }
          if (!messageMatch) {
            messageMatch = errorMessage.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
          }
          errorMessage = messageMatch && messageMatch[1] ? messageMatch[1] : 'You have already submitted this assessment. Please view your existing results in the Dashboard.';
        }
        
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    answers,
    energyBaseline,
    facilityInfo,
    navigate,
    questions,
    region,
    setError,
    type,
    user
  ]);

  // Placeholder for potential future draft/save behavior.
  // Currently a no-op to preserve existing behavior (no save endpoint yet).
  const handleSave = useCallback(async () => {
    logger.log('handleSave called (no-op): assessment draft saving not implemented.');
  }, []);

  return {
    isSubmitting,
    handleSubmitAssessment,
    handleSave
  };
};


